import type { paths, Schemas } from '@ers/sdk';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { api, apiJson, call, isMissingRoute } from '../../api/client';
import { qk } from '../../api/keys';
import { useUsers } from '../../api/org';
import { useSession } from '../../app/session';

/*
 * Облік часу (модуль time): таймер, записи, табелі, погодження, звіти, розклад, відсутності, витрати, ставки й бюджети.
 * Типи — з контракту (@ers/sdk). Таймер і рядок «Час» у картці задачі (хвиля 1) працюють і без модуля на сервері:
 * 404 маршруту — модуль не підключено, елементи обліку часу не показуються.
 */

export type TimeEntry = Schemas['TimeEntry'];
export type TimeProject = Schemas['TimeProject'];
export type WorkType = Schemas['TimeWorkType'];
export type TimeSettings = Schemas['TimeSettings'];
export type TimePolicy = Schemas['TimePolicy'];
export type TimeAbsenceType = Schemas['TimeAbsenceType'];
export type TimeExpenseCategory = Schemas['TimeExpenseCategory'];
export type TimeQualityIssue = Schemas['TimeQualityIssue'];
export type TimeTimesheet = Schemas['TimeTimesheet'];
export type TimeTimesheetView = Schemas['TimeTimesheetView'];

type Json<P extends keyof paths, M extends keyof paths[P]> = paths[P][M] extends {
  responses: { 200: { content: { 'application/json': infer R } } };
}
  ? R
  : paths[P][M] extends { responses: { 201: { content: { 'application/json': infer R } } } }
    ? R
    : never;

export type TaskTimeSummary = Json<'/api/v1/time/tasks/{id}/summary', 'get'>;
export type DescriptionCheck = Json<'/api/v1/time/quality/description', 'post'>;

/** Дані модуля в задачі (task.ext.time). */
export type TaskTimeExt = {
  trackedMinutes: number;
  myMinutes: number;
  estimateMinutes: number | null;
  percentOfEstimate: number | null;
  overEstimate: boolean;
  runningEntryId: string | null;
  runningSince: string | null;
};

/** Ключі кешу модуля: усе під префіксом ['time'] — подія потоку «time.*» оновлює модуль цілком. */
export const timeKeys = {
  all: ['time'] as const,
  timer: ['time', 'timer'] as const,
  summary: (taskId: string) => ['time', 'summary', taskId] as const,
  workTypes: ['time', 'work-types'] as const,
  settings: ['time', 'settings'] as const,
  projects: (params: Record<string, unknown> = {}) => ['time', 'projects', params] as const,
  policies: ['time', 'policies'] as const,
  absenceTypes: ['time', 'absence-types'] as const,
  expenseCategories: ['time', 'expense-categories'] as const,
  entries: (params: Record<string, unknown>) => ['time', 'entries', params] as const,
  quality: (params: Record<string, unknown>) => ['time', 'quality', params] as const,
  timesheetView: (params: Record<string, unknown>) => ['time', 'timesheet-view', params] as const,
  profile: (userId: string) => ['time', 'profile', userId] as const,
  adaptation: ['time', 'adaptation'] as const,
};

/** Поточний таймер. `available` — модуль обліку часу підключено й доступний користувачу. */
export function useTimer() {
  const { can } = useSession();
  const allowed = can('time:read');
  const q = useQuery({
    queryKey: timeKeys.timer,
    enabled: allowed,
    retry: false,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    queryFn: () => apiJson<{ running: TimeEntry | null }>('GET', '/api/v1/time/timer'),
  });
  const missing = isMissingRoute(q.error);
  return { running: q.data?.running ?? null, available: allowed && !!q.data && !missing, isLoading: q.isLoading, error: missing ? null : q.error };
}

/** Модуль доступний (для реєстру модулів: показ кнопок і рядків). */
export function useTimeAvailable(): boolean {
  return useTimer().available;
}

/** Оновити кеш модуля (і картку задачі, якщо запис прив’язано до неї). */
export function useInvalidateTime() {
  const qc = useQueryClient();
  return (taskIds: (string | null | undefined)[] = []) => {
    void qc.invalidateQueries({ queryKey: timeKeys.all });
    for (const id of taskIds) if (id) void qc.invalidateQueries({ queryKey: qk.task(id) });
  };
}

export function useStartTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas['TimeTimerStartInput']) => apiJson<{ entry: TimeEntry; stopped: TimeEntry | null }>('POST', '/api/v1/time/timer/start', body),
    onSuccess: (res, v) => {
      qc.setQueryData(timeKeys.timer, { running: res.entry });
      void qc.invalidateQueries({ queryKey: timeKeys.all });
      if (v.taskId) void qc.invalidateQueries({ queryKey: qk.task(v.taskId) });
      if (res.stopped?.taskId) void qc.invalidateQueries({ queryKey: qk.task(res.stopped.taskId) });
    },
  });
}

export function useStopTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas['TimeTimerStopInput'] = {}) => apiJson<TimeEntry>('POST', '/api/v1/time/timer/stop', body),
    onSuccess: (entry) => {
      qc.setQueryData(timeKeys.timer, { running: null });
      void qc.invalidateQueries({ queryKey: timeKeys.all });
      if (entry.taskId) void qc.invalidateQueries({ queryKey: qk.task(entry.taskId) });
    },
  });
}

export function useTaskTimeSummary(taskId: string, enabled = true) {
  return useQuery({
    queryKey: timeKeys.summary(taskId),
    enabled,
    retry: false,
    staleTime: 30_000,
    queryFn: () => apiJson<TaskTimeSummary>('GET', `/api/v1/time/tasks/${taskId}/summary`),
  });
}

/** Актуальні типи робіт (без архівних). */
export function useWorkTypes(enabled = true) {
  return useQuery({
    queryKey: timeKeys.workTypes,
    enabled,
    retry: false,
    staleTime: 10 * 60_000,
    queryFn: async () => (await apiJson<{ data: WorkType[] }>('GET', '/api/v1/time/work-types')).data.filter((w) => !w.archivedAt),
  });
}

/** Підказка й перевірка опису за стандартом «об'єкт_дія в минулому часі». */
export function checkDescription(description: string, taskId?: string) {
  return apiJson<DescriptionCheck>('POST', '/api/v1/time/quality/description', { description, taskId });
}

export type ManualEntryInput = {
  taskId: string;
  day?: string;
  startTime?: string;
  durationMinutes?: number;
  startedAt?: string;
  endedAt?: string;
  description?: string;
  billable?: boolean;
  workTypeId?: string | null;
  allowSensitive?: boolean;
};

export function useCreateEntry(taskId: string) {
  const invalidate = useInvalidateTime();
  return useMutation({
    mutationFn: (body: ManualEntryInput) => apiJson<TimeEntry>('POST', '/api/v1/time/entries', { ...body, source: 'manual' }),
    onSuccess: () => invalidate([taskId]),
  });
}

/** Поточний час, що оновлюється щосекунди (лише поки `active`). */
export function useNow(active: boolean, intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const t = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(t);
  }, [active, intervalMs]);
  return now;
}

/** Тривалість «гг:хх:сс». */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// ——— Спільні довідники модуля (для всіх сторінок) ———

export function useTimeSettings(enabled = true) {
  return useQuery({
    queryKey: timeKeys.settings,
    enabled,
    staleTime: 5 * 60_000,
    queryFn: () => call(api.GET('/api/v1/time/settings')),
  });
}

/** «Карта проєктів часу»: типово — лише актуальні (кошик «Прив’язати до правильного проєкту» — першим). */
export function useTimeProjects(opts: { includeUnused?: boolean; includeArchived?: boolean } = {}) {
  const query = { includeUnused: opts.includeUnused ? ('true' as const) : undefined, includeArchived: opts.includeArchived ? ('true' as const) : undefined };
  return useQuery({
    queryKey: timeKeys.projects(query),
    staleTime: 5 * 60_000,
    queryFn: async () => (await call(api.GET('/api/v1/time/projects', { params: { query } }))).data,
  });
}

/** Проєкти часу за ідентифікатором (зокрема «не використовується» — для назв у старих записах). */
export function useTimeProjectMap(): Map<string, TimeProject> {
  const q = useTimeProjects({ includeUnused: true, includeArchived: true });
  return useMemo(() => new Map((q.data ?? []).map((p) => [p.id, p])), [q.data]);
}

export function useAllWorkTypes() {
  return useQuery({
    queryKey: [...timeKeys.workTypes, 'all'],
    staleTime: 5 * 60_000,
    queryFn: async () => (await call(api.GET('/api/v1/time/work-types', { params: { query: { includeArchived: 'true' } } }))).data,
  });
}

export function useTimePolicies(enabled = true) {
  return useQuery({
    queryKey: timeKeys.policies,
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () => (await call(api.GET('/api/v1/time/policies'))).data,
  });
}

export function useAbsenceTypes() {
  return useQuery({
    queryKey: timeKeys.absenceTypes,
    staleTime: 10 * 60_000,
    queryFn: async () => (await call(api.GET('/api/v1/time/absence-types'))).data,
  });
}

export function useExpenseCategories(includeArchived = false) {
  return useQuery({
    queryKey: [...timeKeys.expenseCategories, includeArchived],
    staleTime: 10 * 60_000,
    queryFn: async () => (await call(api.GET('/api/v1/time/expense-categories', { params: { query: includeArchived ? { includeArchived: 'true' } : {} } }))).data,
  });
}

/** Обов’язковість обліку й місткість працівника. */
export function useTimeProfile(userId: string | null | undefined) {
  return useQuery({
    queryKey: timeKeys.profile(userId ?? ''),
    enabled: !!userId,
    staleTime: 5 * 60_000,
    queryFn: () => call(api.GET('/api/v1/time/users/{userId}/profile', { params: { path: { userId: userId! } } })),
  });
}

/** Чи погоджує користувач табелі, відсутності й витрати (область time:approve або є підлеглі). */
export function useIsTimeApprover(): boolean {
  const { can, userId, isAdmin } = useSession();
  const users = useUsers();
  return useMemo(() => can('time:approve') || isAdmin || (users.data ?? []).some((u) => u.managerId === userId && u.status === 'active'), [can, isAdmin, users.data, userId]);
}

/**
 * Черга щоденних перевірок адаптації: робочі дні без перевірки для працівників у щоденному режимі,
 * де користувач — контролер, керівник чи адміністратор. Непорожня черга відкриває «Погодження» й контролеру
 * без права погодження (лише вкладку «Адаптація»).
 */
export function useAdaptationQueue(enabled = true) {
  return useQuery({
    queryKey: [...timeKeys.adaptation, 'queue'],
    staleTime: 30_000,
    enabled,
    queryFn: async () => (await call(api.GET('/api/v1/time/adaptation/queue'))).data,
  });
}

/** Підлеглі користувача (прямі й опосередковані) — для вибору працівника в табелі, звітах, розкладі. */
export function useSubordinateIds(): string[] {
  const { userId } = useSession();
  const users = useUsers();
  return useMemo(() => {
    const list = users.data ?? [];
    const out = new Set<string>();
    const walk = (mgr: string) => {
      for (const u of list) if (u.managerId === mgr && !out.has(u.id)) {
        out.add(u.id);
        walk(u.id);
      }
    };
    walk(userId);
    return [...out];
  }, [users.data, userId]);
}

export const TRACKING_MODE_LABELS: Record<Schemas['TimePolicy']['mode'], string> = {
  all_work: 'Увесь робочий час',
  client_work: 'Клієнтська робота',
  optional: 'За бажанням',
};

export const TIME_PROJECT_KIND_LABELS: Record<TimeProject['kind'], string> = {
  client: 'Клієнтський',
  admin: 'Адміністративний',
  internal: 'Внутрішній',
};

export const ENTRY_SOURCE_LABELS: Record<TimeEntry['source'], string> = {
  timer: 'Таймер',
  manual: 'Вручну',
  calendar: 'Календар',
  ai: 'Чернетка ШІ',
  import: 'Імпорт',
};

export const ENTRY_STATUS_LABELS: Record<TimeEntry['status'], string> = {
  draft: 'Не подано',
  submitted: 'У табелі на погодженні',
  approved: 'Погоджено',
};

export const TIMESHEET_STATUS_LABELS: Record<TimeTimesheet['status'], string> = {
  draft: 'Не подано',
  submitted: 'На погодженні',
  approved_l1: 'Погоджено першим рівнем',
  approved: 'Погоджено',
  returned: 'Повернено',
};
